import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, KeyRound, RefreshCw, Signal, Trash2, Wifi, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import CopyButton from "@/components/CopyButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Owner settings card for the DialFlow GSM HAT (Raspberry Pi + SIM).
// The Pi authenticates with a device token generated here; the token is
// shown exactly once. While the gateway is online and enabled, employee
// "Call" taps go through the SIM instead of Exotel.
function ago(iso) {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return new Date(iso).toLocaleString();
}

function signalLabel(dbm) {
  if (dbm == null) return "—";
  const q = dbm >= -75 ? "excellent" : dbm >= -85 ? "good" : dbm >= -95 ? "fair" : "weak";
  return `${dbm} dBm (${q})`;
}

export default function PhoneGatewayCard() {
  const [gw, setGw] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/gateway");
      setGw(data);
    } catch {
      /* handled by interceptor */
    }
  };

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
    api.get("/employees").then(({ data }) => setEmployees(data || [])).catch(() => {});
  }, []);

  // Employees who can be rung: they need a phone number.
  const ringable = (dept) =>
    employees.filter(
      (e) => e.phone && e.status !== "disabled" && (e.department || "").toLowerCase() === dept.toLowerCase()
    ).length;

  const saveRouting = async (change) => {
    try {
      const { data } = await api.patch("/gateway", change);
      setGw(data);
      toast.success(
        data.inbound_department
          ? `Incoming calls ring ${data.inbound_department} first${data.inbound_fallback ? ", then everyone" : " only"}.`
          : "Incoming calls ring everyone in turn."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gateway/token");
      setToken(data.token);
      setGw(data);
      toast.success("Token created. Paste it into the gateway's config file.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create a token");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (enabled) => {
    try {
      const { data } = await api.patch("/gateway", { enabled });
      setGw(data);
      toast.success(enabled ? "Calls will use the phone gateway" : "Phone gateway paused; calls use Exotel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete("/gateway");
      setToken("");
      await load();
      toast.success("Phone gateway disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    }
  };

  const st = gw?.status || {};

  return (
    <>
      <div className="mt-14 mb-6">
        <h1 className="font-heading text-3xl font-bold">Phone gateway</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Use your own SIM card for calls: either the DialFlow GSM HAT on a Raspberry Pi (click-to-call,
          inbound IVR and recordings) or a spare Android phone with the DialFlow Gateway app
          (click-to-call only). While it&apos;s online, employees&apos; &ldquo;Call&rdquo; taps go through
          the SIM. If it goes offline, calls fall back to Exotel.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none" data-testid="gateway-card">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Cpu className="w-5 h-5 text-[#0055FF]" />
            {st.agent === "android" ? `Android phone${st.device ? ` · ${st.device}` : ""}` : "Raspberry Pi gateway"}
            {gw?.configured && (
              <span
                className={`mono text-[10px] uppercase flex items-center gap-1 ml-2 ${
                  gw.online ? "text-emerald-700" : "text-slate-500"
                }`}
                data-testid="gateway-online"
              >
                {gw.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {gw.online ? "online" : "offline"}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          {token && (
            <div className="border-l-4 border-l-amber-500 bg-amber-50/70 rounded p-4 space-y-2" data-testid="gateway-token">
              <div className="font-heading text-amber-900">Copy this token now. It won&apos;t be shown again.</div>
              <div className="flex items-center gap-2">
                <code className="mono text-xs bg-white border rounded px-2 py-1 break-all">{token}</code>
                <CopyButton value={token} label="Copy token" />
              </div>
              <p className="mono text-[11px] text-slate-600">
                On the Pi, put it in <span className="text-slate-900">/etc/dialflow-gateway/gateway.ini</span> under{" "}
                <span className="text-slate-900">[server] token</span>, then run{" "}
                <span className="text-slate-900">sudo systemctl restart dialflow-gateway</span>.
              </p>
            </div>
          )}

          {gw?.configured ? (
            <>
              <div className="mono text-[11px] text-slate-700 grid md:grid-cols-2 gap-x-6 gap-y-1">
                <div>
                  <span className="text-slate-500">Last seen: </span>
                  {ago(gw.last_seen)}
                </div>
                <div>
                  <span className="text-slate-500">SIM number: </span>
                  {st.own_number || "—"}
                </div>
                <div>
                  <span className="text-slate-500">Network: </span>
                  {st.operator || "—"} {st.registered === false && "(not registered)"}
                </div>
                <div className="flex items-center gap-1">
                  <Signal className="w-3 h-3 text-slate-500" />
                  <span className="text-slate-500">Signal: </span>
                  {signalLabel(st.signal_dbm)}
                </div>
                <div>
                  <span className="text-slate-500">Status: </span>
                  {st.in_call ? "on a call" : st.sim_ready === false ? "SIM not ready" : "idle"}
                </div>
                <div>
                  <span className="text-slate-500">Token: </span>
                  {gw.token_hint || "—"}
                </div>
                {st.battery_pct != null && (
                  <div>
                    <span className="text-slate-500">Battery: </span>
                    {st.battery_pct}% {st.charging ? "(charging)" : "(not charging!)"}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border rounded p-3">
                <div>
                  <div className="font-medium text-sm">Route calls through the gateway</div>
                  <div className="text-xs text-slate-500">Turn off to send all calls through Exotel again.</div>
                </div>
                <Switch checked={!!gw.enabled} onCheckedChange={toggle} data-testid="gateway-enabled" />
              </div>

              <div className="border rounded p-3 space-y-3" data-testid="gateway-inbound-routing">
                <div>
                  <div className="font-medium text-sm">Incoming calls ring</div>
                  <div className="text-xs text-slate-500">
                    Send customers&apos; calls to one department. Whoever answers can transfer the call
                    to a colleague from their app.
                  </div>
                </div>
                <select
                  value={gw.inbound_department || ""}
                  onChange={(e) => saveRouting({ inbound_department: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="gateway-inbound-department"
                >
                  <option value="">Everyone, in turn</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d} ({ringable(d)} {ringable(d) === 1 ? "person" : "people"})
                    </option>
                  ))}
                </select>
                {gw.inbound_department && ringable(gw.inbound_department) === 0 && (
                  <div className="text-xs text-amber-700">
                    Nobody in {gw.inbound_department} has a phone number, so calls ring everyone.
                  </div>
                )}
                {gw.inbound_department && (
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-600">
                      If nobody in {gw.inbound_department} answers, ring everyone else
                    </div>
                    <Switch
                      checked={gw.inbound_fallback !== false}
                      onCheckedChange={(v) => saveRouting({ inbound_fallback: v })}
                      data-testid="gateway-inbound-fallback"
                    />
                  </div>
                )}
                {st.agent && st.agent !== "android" && (
                  <div className="text-xs text-slate-500">
                    The Raspberry Pi gateway follows the IVR menu instead (IVR Builder).
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={load} data-testid="gateway-refresh">
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={busy} data-testid="gateway-rotate">
                      <KeyRound className="w-4 h-4 mr-1" /> New token
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Create a new token?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The gateway disconnects immediately and stays offline until you put the new
                        token in its config file.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={generate}>Create new token</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600" data-testid="gateway-delete">
                      <Trash2 className="w-4 h-4 mr-1" /> Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect the phone gateway?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Its token stops working and calls go back to Exotel. Past calls and recordings are kept.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">
                        Disconnect
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          ) : (
            <>
              <ol className="list-decimal list-inside text-sm text-slate-700 space-y-1">
                <li>
                  <strong>Raspberry Pi:</strong> fit the GSM HAT and a SIM, then run{" "}
                  <span className="mono text-xs">sudo ./install.sh</span> from the{" "}
                  <span className="mono text-xs">pi-agent</span> folder.
                </li>
                <li>
                  <strong>Or an Android phone:</strong> install the DialFlow Gateway app and follow its setup screen.
                </li>
                <li>Generate a token below and paste it into the Pi&apos;s config file or the Android app.</li>
              </ol>
              <div className="flex justify-end">
                <Button
                  onClick={generate}
                  disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="gateway-generate"
                >
                  <KeyRound className="w-4 h-4 mr-1" /> Generate token
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
